import { splitArray } from './arrays'

const resortArray = (array, { data = {}, sortByKey = null, sortByMethod = null, sortOrder = null }) => {
  if (sortByKey && array.length) {
    const ascending = 'asc' === sortOrder
    // eslint-disable-next-line unicorn/prefer-ternary
    if ('letters' === sortOrder) {
      return array.sort((a, b) => {
        // Extract data value objects
        const [aLetter, bLetter] = [a.letter || '', b.letter || '']
        // Sort strings
        return aLetter.length === bLetter.length ? aLetter.localeCompare(bLetter) : aLetter.length - bLetter.length
      })
    } else {
      return array.sort((a, b) => {
        // Extract data value objects
        const [aSource, bSource] = [data[a.uid]?.attributes?.[sortByKey], data[b.uid]?.attributes?.[sortByKey]]
        // If sortByMethod defined then get data from metrics structure
        const [aData, bData] = [
          (sortByMethod ? aSource?.metrics?.[sortByMethod] : aSource) ?? {},
          (sortByMethod ? bSource?.metrics?.[sortByMethod] : bSource) ?? {}
        ]
        if ('date' === aData.type) {
          // Sort dates
          const [aValue, bValue] = [new Date(aData.value).getTime() ?? 0, new Date(bData.value).getTime() ?? 0]
          return ascending ? aValue - bValue : bValue - aValue
        } else if ('string' === aData.type) {
          // Sort strings
          const [aValue, bValue] = [aData.value ? `${aData.value}` : '', bData.value ? `${bData.value}` : '']
          return ascending ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue)
        } else if (['surveyTitle', 'surveys', 'surveyId'].includes(aData.type)) {
          // Sort array of string type values
          const [aValue, bValue] = [`${aData?.value?.[0]?.id || ''}`, `${bData?.value?.[0]?.id || ''}`]
          return ascending ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue)
        } else if ('tags' === aData.type) {
          // Sort tags
          const [aValue, bValue] = [
            (aData.value && Array.isArray(aData.value) ? aData.value : []).join(''),
            (bData.value && Array.isArray(bData.value) ? bData.value : []).join('')
          ]
          return ascending ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue)
        } else {
          // Sort numeric
          const [aValue, bValue] = [aData.value ?? -1, bData.value ?? -1]
          return ascending ? aValue - bValue : bValue - aValue
        }
      })
    }
  } else return array
}

/**
 * Resorts items array using data values from 'data' argument by 'sortOrder' order.
 * item.uid as subject uid and 'sortByAttribute' argument are used to find sort by value
 * in 'data' object. Pinned items (benchmarks) and not pinned items are sorted separately.
 *
 * @param items - Array of items to sort
 * @param data - Items data for sorting
 * @param sortByKey - Sort by attribute key
 * @param sortOrder - Sort order: asc / desc
 * @returns {Array} - Array of sorted items
 */
export const resort = ({ items = [], data = {}, sortByKey = null, sortByMethod = null, sortOrder = null }) => {
  const sortAlowed = sortOrder && 'manual' !== sortOrder
  // Clean up items without data
  const filtered = items.map(item => ((item.new = false), item))
  // Collect pinned and unpinned
  const [pinnedItems, looseItems] = splitArray(filtered, item => item.pinned)
  // Sort pinned
  const sortedPinned = sortAlowed ? resortArray(pinnedItems, { data, sortByKey, sortByMethod, sortOrder }) : pinnedItems
  // Sort unpinned
  const sortedLooseItems = sortAlowed
    ? resortArray(looseItems, { data, sortByKey, sortByMethod, sortOrder })
    : looseItems
  // Return result. Pinned are first.
  return [...sortedPinned, ...sortedLooseItems]
}

const compareSubjects = (subjects, a, b) => {
  const [a1, b1, a2, b2] = [
    subjects[a.uid]?.wave?.index || 0,
    subjects[b.uid]?.wave?.index || 0,
    subjects[a.uid]?.split?.index || 0,
    subjects[b.uid]?.split?.index || 0
  ]
  return a1 === b1 ? a2 - b2 : a1 - b1
}

export const reorder = ({ items = [], subjects = {}, separate = false }) => {
  // Clean up items without data
  const filtered = items.map(item => ((item.new = false), item))
  if (separate) {
    // Collect pinned
    const pinned = filtered.filter(item => item.pinned)
    // Collect unpinned
    const loosed = filtered.filter(item => !item.pinned)
    // Sort pinned
    const sortedPinned = pinned.sort((a, b) => compareSubjects(subjects, a, b))
    // Sort unpinned
    const sortedLoosed = loosed.sort((a, b) => compareSubjects(subjects, a, b))
    // Return result. Pinned are first.
    return [...sortedPinned, ...sortedLoosed]
  } else {
    // Sort
    const sorted = filtered.sort((a, b) => compareSubjects(subjects, a, b))
    // Return result. Pinned are first.
    return sorted
  }
}

export const sortObject = (object, options) => {
  if (undefined === object || null === object) return object
  if (Array.isArray(object))
    return object
      .map(v => sortObject(v, options))
      .sort((a, b) => {
        const [aNum, bNum] = [undefined === a ? 0 : Number(a), undefined === b ? 0 : Number(b)]
        const [aVal, bVal] = [isNaN(aNum) ? JSON.stringify(a) : aNum, isNaN(bNum) ? JSON.stringify(b) : bNum]
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0
      })
  else if ('object' === typeof object) {
    const { include, exclude, ...nextOptions } = options || {}
    return nextOptions && nextOptions.selectors && nextOptions.selectors[nextOptions.key]
      ? nextOptions.selectors[nextOptions.key](object)
      : Object.keys(object)
          .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
          .reduce((result, key) => {
            const skip = (exclude && exclude.includes(key)) || (include && !include.includes(key))
            if (!skip) result[key] = sortObject(object[key], { ...nextOptions, key })
            return result
          }, {})
  } else return object
}

export const compareObjects = (a, b, options) =>
  JSON.stringify(sortObject(a, options)) === JSON.stringify(sortObject(b, options))
